"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
/** ↑ ↑ ↓ ↓ ← → ← → B A — the classic. */
const KONAMI_SEQUENCE = [
  "arrowup",
  "arrowup",
  "arrowdown",
  "arrowdown",
  "arrowleft",
  "arrowright",
  "arrowleft",
  "arrowright",
  "b",
  "a",
] as const;

/** Quick toggle: hold `.` and `,` together (outside any input focus). */
const CHORD_KEYS = [".", ","] as const;

const STORAGE_KEY = "resurrection-wow-mode";

interface WowModeContextValue {
  active: boolean;
  setActive: (active: boolean) => void;
}

const WowModeContext = createContext<WowModeContextValue | null>(null);

export function useWowMode(): WowModeContextValue {
  const context = useContext(WowModeContext);
  if (!context) {
    throw new Error("useWowMode must be used within WowModeProvider");
  }
  return context;
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target.isContentEditable
  );
}

export function WowModeProvider({ children }: { children: React.ReactNode }) {
  const [active, setActiveState] = useState(false);
  const konamiProgressRef = useRef(0);
  const heldKeysRef = useRef(new Set<string>());

  const setActive = useCallback((next: boolean) => {
    setActiveState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next ? "1" : "0");
    } catch {
      // private browsing — mode simply won't persist
    }
  }, []);

  const toggle = useCallback(() => {
    setActiveState((previous) => {
      const next = !previous;
      try {
        localStorage.setItem(STORAGE_KEY, next ? "1" : "0");
      } catch {
        // ignore
      }
      return next;
    });
  }, []);

  useEffect(() => {
    try {
      if (localStorage.getItem(STORAGE_KEY) === "1") {
        setActiveState(true);
      }
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("wow-mode", active);
    root.classList.toggle("wc-human-cursor", active);
    return () => {
      root.classList.remove("wow-mode", "wc-human-cursor");
    };
  }, [active]);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      const key = event.key.toLowerCase();
      const typing = isTypingTarget(event.target);

      // `.` + `,` chord — both keys held at once, outside input focus.
      if ((CHORD_KEYS as readonly string[]).includes(key) && !typing) {
        if (!event.repeat) {
          const other = key === "." ? "," : ".";
          if (heldKeysRef.current.has(other)) {
            event.preventDefault();
            toggle();
          }
          heldKeysRef.current.add(key);
        }
      }

      // Konami sequence.
      const expected = KONAMI_SEQUENCE[konamiProgressRef.current];

      // Letters shouldn't hijack typing in inputs; arrows are fine anywhere.
      if ((key === "b" || key === "a") && typing) {
        konamiProgressRef.current = 0;
        return;
      }

      if (key === expected) {
        konamiProgressRef.current += 1;
        if (konamiProgressRef.current === KONAMI_SEQUENCE.length) {
          konamiProgressRef.current = 0;
          toggle();
        }
      } else {
        // Allow restarting mid-sequence when the key matches the first entry.
        konamiProgressRef.current = key === KONAMI_SEQUENCE[0] ? 1 : 0;
      }
    }

    function handleKeyUp(event: KeyboardEvent) {
      heldKeysRef.current.delete(event.key.toLowerCase());
    }

    function handleBlur() {
      heldKeysRef.current.clear();
    }

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    window.addEventListener("blur", handleBlur);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("blur", handleBlur);
    };
  }, [toggle]);

  const value = useMemo(() => ({ active, setActive }), [active, setActive]);

  return (
    <WowModeContext.Provider value={value}>{children}</WowModeContext.Provider>
  );
}
